// Coalesces the profile-picture self-fetches of many ContactAvatars into one
// request per burst.
//
// Every ContactAvatar that mounts without a picture used to dispatch its own
// `fetchAndCacheProfilePicUrlsThunk([oneId])` — one round trip to the engine
// tool AND one cache-document PATCH per avatar. A list that mounts ten rows at
// once (the planner sidebar on first paint, or on every scroll that brings new
// rows in) therefore sent ten requests where one carries the same ids: the tool
// and the thunk both take an array. Measured on the planner sidebar: one
// `waGetProfilePicUrlTool` POST plus one `wa-contacts-cache` PATCH per contact
// scrolled into view.
//
// De-duplication is unchanged and still owned by profilePicFetchRegistry: the
// caller marks an id as requested BEFORE queueing it, exactly as it did before
// dispatching, so an id is never queued twice. Only the transport is batched.

export type ProfilePicSender = (ids: string[]) => Promise<unknown>;

export interface ProfilePicBatcherOptions {
    /** How long to wait for more ids after the first one arrives. */
    windowMs?: number;
    /** Never put more ids than this in one request (the lazy-load hook uses 20). */
    maxBatch?: number;
    /** Called with the ids of a batch whose request failed, so they may be retried. */
    onFailure?: (ids: string[]) => void;
    schedule?: (fn: () => void, ms: number) => unknown;
}

export interface ProfilePicBatcher<K> {
    /** Queue one id for the sender identified by `key`. */
    request(key: K, send: ProfilePicSender, id: string): void;
    /** Send everything queued now (tests; also safe to call at any time). */
    flush(): void;
}

/**
 * A batcher keyed by SENDER, not global: the extension content script runs two
 * redux stores side by side, and an id queued through one store's dispatch must
 * be fetched and cached through that same store.
 */
export function createProfilePicBatcher<K>(options: ProfilePicBatcherOptions = {}): ProfilePicBatcher<K> {
    const windowMs = options.windowMs ?? 20;
    const maxBatch = Math.max(1, options.maxBatch ?? 20);
    const schedule = options.schedule ?? ((fn, ms) => setTimeout(fn, ms));
    const queues = new Map<K, { send: ProfilePicSender; ids: string[] }>();
    let scheduled = false;

    const flush = () => {
        scheduled = false;
        const pending = [...queues.values()];
        queues.clear();
        for (const { send, ids } of pending) {
            for (let i = 0; i < ids.length; i += maxBatch) {
                const chunk = ids.slice(i, i + maxBatch);
                let sent: Promise<unknown>;
                try { sent = send(chunk); } catch (error) { sent = Promise.reject(error); }
                Promise.resolve(sent).catch(() => options.onFailure?.(chunk));
            }
        }
    };

    return {
        request(key, send, id) {
            if (!id) return;
            const queue = queues.get(key) ?? { send, ids: [] };
            queue.send = send;
            if (!queue.ids.includes(id)) queue.ids.push(id);
            queues.set(key, queue);
            if (!scheduled) {
                scheduled = true;
                schedule(flush, windowMs);
            }
        },
        flush,
    };
}
